import * as Sentry from '@sentry/react';
import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { LandingRedirect, MainLayout } from '~/layouts';
import { PermissionFilter } from '../components/PermissionFilter';
import { AuditLogDetails, AuditLogList } from './AuditLogs';
import { FontAuditLogs, FontList, FontSettings } from './Fonts';
import { NetworkSettings } from './NetworkSettings';
import { UserProfile } from './UserProfile';
import { AnnouncementDetail, AnnouncementListPage, AnnouncementsBreadcrumb } from './announcements';
import { ChannelGuideDetailPage } from './channel-guides/detail';
import { ChannelGuideAuditLogs } from './channel-guides/detail/AuditLogs';
import { ChannelGuideSettings } from './channel-guides/detail/settings';
import { ChannelGuideList } from './channel-guides/list';
import { ChannelSettings } from './channels/detail';
import { ChannelAnalysis } from './channels/detail/Analysis';
import { ChannelAuditLogs } from './channels/detail/AuditLogs';
import { ChannelList } from './channels/list';
import { NewChannel } from './channels/new';
import {
  ContentFolderView,
  ContentRoot,
  ContentRootRedirect,
  ContentSearch,
  EditApp,
  MediaItemAuditLogs,
  MediaItemDetails,
  MediaItemThumbnails,
  MediaItemUsage,
  MediaItemVersions,
  MediaItemView,
} from './content';
import { CreatePassword } from './create-password';
import { Dashboard } from './dashboard';
import {
  DataSourceAuditLogs,
  DataSourceData,
  DataSourceDetails,
  DataSourcesList,
} from './data-sources';
import { DeviceGroupList } from './device-groups';
import { DeviceGroupSettings } from './device-groups/detail';
import { DeviceGroupAuditLogs } from './device-groups/detail/AuditLogs';
import { DeviceGroupDevices } from './device-groups/detail/devices';
import { DeviceSignalSettings } from './device-signals/detail';
import { DeviceSignalAuditLogs } from './device-signals/detail/AuditLogs';
import { DeviceSignalList } from './device-signals/list';
import { NewDeviceSignal } from './device-signals/new';
import { ActivateDevice } from './devices/activate';
import { DeviceList } from './devices/list';
import { DeviceDashboard } from './devices/show';
import { DeviceAuditLogs } from './devices/show/AuditLogs';
import { DeviceCommands } from './devices/show/DeviceCommands';
import { DeviceServices } from './devices/show/Services';
import { DevicePreview } from './devices/show/preview';
import { DeviceSchedule } from './devices/show/schedule';
import { DeviceSettings } from './devices/show/settings';
import { EventTypeList, EventTypeSettings } from './event-types';
import { EventTypeAuditLogs } from './event-types/detail/AuditLogs';
import { EventTypeEvents } from './event-types/detail/events';
import { EventTypeShows } from './event-types/detail/shows';
import { NewEventType } from './event-types/new';
import { Scheduler } from './events';
import { ScheduleShowDetailPage as EventScheduleShowDetailPage } from './events/show';
import { ScheduleShowPreviewPage as EventScheduleShowPreviewPage } from './events/show/preview';
import { ScheduleShowSettings as EventScheduleShowSettings } from './events/show/settings';
import { ForgotPassword } from './forgot-password';
import { EditLayout, LayoutAuditLogs, LayoutsList } from './layouts';
import { LiveChannels } from './live-channels';
import { ChannelDetail } from './live-channels/ChannelDetail';
import { Login } from './login';
import { NotFound } from './not-found';
import { PlaylistsRoot } from './playlists/PlaylistsRoot';
import { PlaylistsRootRedirect } from './playlists/PlaylistsRootRedirect';
import { PlaylistDetailPage } from './playlists/detail';
import { PlaylistAuditLogs } from './playlists/detail/AuditLogs';
import { PlaylistPreviewPage } from './playlists/detail/preview';
import { PlaylistSettings } from './playlists/detail/settings';
import { PlaylistUsage } from './playlists/detail/usage';
import { PlaylistList } from './playlists/list';
import { PlaylistSearch } from './playlists/search';
import { PlayoutReports } from './playout-reports';
import {
  PlayoutReportsByAsset,
  PlayoutReportsByAssetDetail,
  PlayoutReportsBySponsor,
  PlayoutReportsBySponsorDetail,
  PlayoutReportsOverview,
} from './playout-reports/tabs';
import { Remote } from './remote';
import { ResetPassword } from './reset-password';
import {
  RoleAuditLogs,
  RoleContentFolderList,
  RoleDataSourceList,
  RoleDesignFolderList,
  RoleDeviceList,
  RoleList,
  RolePlaylistFolderList,
  RoleSettings,
  RoleShowFolderList,
  RoleUserList,
} from './roles';
import { RoleEventTypeList } from './roles/RoleEventTypeList/RoleEventTypeList';
import { ScheduleShowDetails } from './schedules/detail';
import { ScheduleList } from './schedules/list';
import { ShowsRoot } from './shows/ShowsRoot';
import { ShowsRootRedirect } from './shows/ShowsRootRedirect';
import { ShowDetailPage } from './shows/detail';
import { ShowAuditLogs } from './shows/detail/AuditLogs';
import { ShowPreviewPage } from './shows/detail/ShowPreview';
import { ShowSettings } from './shows/detail/ShowSettings';
import { ShowList } from './shows/list';
import { ShowSearch } from './shows/search';
import { StudioEdit, StudioLayout, StudioList, StudioRoot, StudioRootRedirect } from './studio';
import { DesignSearch } from './studio/search';
import { CreateTemplate, TemplateList } from './templates';
import { TemplateConfiguration } from './templates/detail/configuration';
import { TemplateSettings } from './templates/detail/settings';
import { TemplateUsage } from './templates/detail/usage';
import { TemplateVersions } from './templates/detail/versions';
import { TVBrandSettings } from './tv-brands/detail';
import { TVBrandAuditLogs } from './tv-brands/detail/AuditLogs';
import { TVBrandList } from './tv-brands/list';
import { NewTVBrand } from './tv-brands/new';
import { UserList, UserSettings } from './users';
import { UserAuditLogs } from './users/detail/AuditLogs';

Sentry.init({
  dsn: 'https://1c9857e7842b483cab4f3121b8f4c9bf@o1130152.ingest.sentry.io/6178115',
  enabled: import.meta.env.PROD,
  environment: window.location.hostname.includes('staging') ? 'staging' : 'production',
  release: import.meta.env.SENTRY_RELEASE,
});

export const router = Sentry.wrapCreateBrowserRouter(createBrowserRouter)(
  createRoutesFromElements(
    <Route>
      <Route path="create-password/:token" element={<CreatePassword />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-password/:token" element={<ResetPassword />} />
      <Route path="login" element={<Login />} />
      <Route path="remote" element={<Remote />} />

      <Route path="/" element={<MainLayout />}>
        <Route path="activate" />

        <Route path="networks/:networkId">
          <Route index element={<LandingRedirect />} />

          <Route
            path="activate"
            element={<Navigate replace to={`../devices/activate${location.search}`} />}
          />

          <Route path="audit-logs" element={<PermissionFilter permission="canManage" />}>
            <Route index element={<AuditLogList />} />
            <Route path=":auditLogId">
              <Route path="details" element={<AuditLogDetails />} />
              <Route path="records" element={<AuditLogDetails />} />
            </Route>
          </Route>

          <Route
            path="announcements"
            handle={{
              breadcrumb: <AnnouncementsBreadcrumb />,
            }}
          >
            <Route index element={<AnnouncementListPage />} />
            <Route path=":announcementId" element={<AnnouncementDetail />} />
          </Route>

          <Route path="dashboard" element={<Dashboard />} />

          <Route path="channel-guides" element={<PermissionFilter permission="canManage" />}>
            <Route index element={<ChannelGuideList />} />
            <Route path=":channelGuideId">
              <Route index element={<Navigate to="channels" replace />} />
              <Route path="channels" element={<ChannelGuideDetailPage />} />
              <Route path="settings" element={<ChannelGuideSettings />} />
              <Route element={<PermissionFilter permission="canManage" />}>
                <Route path="audit-logs" element={<ChannelGuideAuditLogs />} />
              </Route>
            </Route>
          </Route>

          <Route path="channels" element={<PermissionFilter permission="canManage" />}>
            <Route index element={<ChannelList />} />
            <Route path="new" element={<NewChannel />} />
            <Route path=":channelId">
              <Route index element={<Navigate replace to="details" />} />
              <Route path="details" element={<ChannelSettings />} />
              <Route path="analysis" element={<ChannelAnalysis />} />
              <Route path="audit-logs" element={<ChannelAuditLogs />} />
            </Route>
          </Route>

          <Route
            path="data-sources"
            element={<PermissionFilter permission="canAccessDataSources" />}
          >
            <Route index element={<DataSourcesList />} />
            <Route path=":dataSourceId">
              <Route index element={<Navigate replace to="edit" />} />
              <Route path="edit" element={<DataSourceDetails />} />
              <Route path="data" element={<DataSourceData />} />
              <Route element={<PermissionFilter permission="canManage" />}>
                <Route path="audit-logs" element={<DataSourceAuditLogs />} />
              </Route>
            </Route>
          </Route>

          <Route path="devices" element={<PermissionFilter permission="canAccessDevices" />}>
            <Route index element={<DeviceList />} />
            <Route element={<PermissionFilter permission="canActivateDevices" />}>
              <Route path="activate" element={<ActivateDevice />} />
            </Route>
            <Route path=":deviceId">
              <Route index element={<Navigate replace to="details" />} />
              <Route path="details" element={<DeviceDashboard />} />
              <Route path="preview" element={<DevicePreview />} />
              <Route path="schedule" element={<DeviceSchedule />} />
              <Route path="settings" element={<DeviceSettings />} />
              <Route element={<PermissionFilter permission="canManage" />}>
                <Route path="audit-logs" element={<DeviceAuditLogs />} />
                <Route path="commands" element={<DeviceCommands />} />
                <Route path="services" element={<DeviceServices />} />
              </Route>
            </Route>
          </Route>

          <Route path="device-groups" element={<PermissionFilter permission="canManage" />}>
            <Route index element={<DeviceGroupList />} />
            <Route path=":deviceGroupId">
              <Route index element={<Navigate replace to="details" />} />
              <Route path="details" element={<DeviceGroupSettings />} />
              <Route path="devices" element={<DeviceGroupDevices />} />
              <Route path="audit-logs" element={<DeviceGroupAuditLogs />} />
            </Route>
          </Route>

          <Route path="device-signals" element={<PermissionFilter permission="canManage" />}>
            <Route index element={<DeviceSignalList />} />
            <Route path="new" element={<NewDeviceSignal />} />
            <Route path=":deviceSignalId">
              <Route index element={<Navigate replace to="details" />} />
              <Route path="details" element={<DeviceSignalSettings />} />
              <Route path="audit-logs" element={<DeviceSignalAuditLogs />} />
            </Route>
          </Route>

          <Route path="events" element={<PermissionFilter permission="canAccessScheduler" />}>
            <Route index element={<Scheduler />} />
            <Route path="shows/:showFolderId/show/:showId" element={<ShowsRoot />}>
              <Route path="design" element={<EventScheduleShowDetailPage />} />
              <Route path="preview" element={<EventScheduleShowPreviewPage />} />
              <Route path="settings" element={<EventScheduleShowSettings />} />
              <Route element={<PermissionFilter permission="canManage" />}>
                <Route path="audit-logs" element={<ShowAuditLogs />} />
              </Route>
            </Route>
          </Route>

          <Route path="event-types" element={<PermissionFilter permission="canManage" />}>
            <Route index element={<EventTypeList />} />
            <Route path="new" element={<NewEventType />} />
            <Route path=":eventTypeId">
              <Route index element={<Navigate replace to="details" />} />
              <Route path="details" element={<EventTypeSettings />} />
              <Route path="events" element={<EventTypeEvents />} />
              <Route path="shows" element={<EventTypeShows />} />
              <Route path="audit-logs" element={<EventTypeAuditLogs />} />
            </Route>
          </Route>

          <Route path="fonts" element={<PermissionFilter permission="canAccessFonts" />}>
            <Route index element={<FontList />} />
            <Route path=":fontId">
              <Route index element={<Navigate replace to="details" />} />
              <Route path="details" element={<FontSettings />} />
              <Route element={<PermissionFilter permission="canManage" />}>
                <Route path="audit-logs" element={<FontAuditLogs />} />
              </Route>
            </Route>
          </Route>

          <Route element={<PermissionFilter permission="canAccessLiveChannelStreams" />}>
            <Route path="live-channels">
              <Route index element={<LiveChannels />} />
              <Route path=":channelId" element={<ChannelDetail />} />
            </Route>
          </Route>

          <Route path="layouts" element={<PermissionFilter permission="canManage" />}>
            <Route index element={<LayoutsList />} />
            <Route path=":layoutId">
              <Route index element={<Navigate replace to="design" />} />
              <Route path="design" element={<EditLayout />} />
              <Route element={<PermissionFilter permission="canManage" />}>
                <Route path="audit-logs" element={<LayoutAuditLogs />} />
              </Route>
            </Route>
          </Route>

          <Route path="content" element={<ContentRoot />}>
            <Route index element={<ContentRootRedirect />} />
            <Route path="search" element={<ContentSearch />} />
            <Route path=":contentFolderId">
              <Route index element={<ContentFolderView />} />
              <Route path="apps/:appId" element={<EditApp />}>
                <Route index element={<Navigate replace to="settings" />} />
                <Route path="details" />
                <Route path="settings" />
                <Route path="usage" />
                <Route element={<PermissionFilter permission="canManage" />}>
                  <Route path="audit-logs" />
                </Route>
              </Route>
              <Route path="media/:mediaItemId" element={<MediaItemView />}>
                <Route index element={<Navigate replace to="details" />} />
                <Route path="details" element={<MediaItemDetails />} />
                <Route path="thumbnails" element={<MediaItemThumbnails />} />
                <Route path="renditions" element={<MediaItemVersions />} />
                <Route path="usage" element={<MediaItemUsage />} />
                <Route element={<PermissionFilter permission="canManage" />}>
                  <Route path="audit-logs" element={<MediaItemAuditLogs />} />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route element={<PermissionFilter permission="canAccessPlaylists" />}>
            <Route path="playlists" element={<PlaylistsRoot />}>
              <Route index element={<PlaylistsRootRedirect />} />
              <Route path="search" element={<PlaylistSearch />} />
              <Route path=":playlistFolderId">
                <Route index element={<PlaylistList />} />
                <Route path="playlist/:playlistId">
                  <Route index element={<Navigate replace to="design" />} />
                  <Route path="design" element={<PlaylistDetailPage />} />
                  <Route path="preview" element={<PlaylistPreviewPage />} />
                  <Route path="settings" element={<PlaylistSettings />} />
                  <Route path="usage" element={<PlaylistUsage />} />
                  <Route element={<PermissionFilter permission="canManage" />}>
                    <Route path="audit-logs" element={<PlaylistAuditLogs />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="profile" element={<UserProfile />} />

          <Route
            path="playout-reports"
            element={<PermissionFilter permission="canAccessReporting" />}
          >
            <Route element={<PlayoutReports />}>
              <Route index element={<PlayoutReportsOverview />} />
              <Route path="by-sponsor" element={<PlayoutReportsBySponsor />} />
              <Route path="by-sponsor/:sponsorId" element={<PlayoutReportsBySponsorDetail />} />
              <Route path="by-asset" element={<PlayoutReportsByAsset />} />
              <Route path="by-asset/:kind/:assetId" element={<PlayoutReportsByAssetDetail />} />
            </Route>
          </Route>

          <Route path="roles">
            <Route index element={<RoleList />} />
            <Route path=":roleId">
              <Route index element={<Navigate replace to="details" />} />
              <Route path="audit-logs" element={<RoleAuditLogs />} />
              <Route path="content-folders" element={<RoleContentFolderList />} />
              <Route path="data-sources" element={<RoleDataSourceList />} />
              <Route path="devices" element={<RoleDeviceList />} />
              <Route path="details" element={<RoleSettings />} />
              <Route path="event-types" element={<RoleEventTypeList />} />
              <Route path="design-folders" element={<RoleDesignFolderList />} />
              <Route path="playlist-folders" element={<RolePlaylistFolderList />} />
              <Route path="show-folders" element={<RoleShowFolderList />} />
              <Route path="users" element={<RoleUserList />} />
            </Route>
          </Route>

          <Route path="settings" element={<PermissionFilter permission="canManage" />}>
            <Route index element={<NetworkSettings />} />
          </Route>

          <Route path="schedules" element={<PermissionFilter permission="canAccessScheduler" />}>
            <Route index element={<ScheduleList />} />
            <Route path=":scheduledShowId">
              <Route path="details" element={<ScheduleShowDetails />} />
            </Route>
          </Route>

          <Route element={<PermissionFilter permission="canAccessShows" />}>
            <Route path="shows" element={<ShowsRoot />}>
              <Route index element={<ShowsRootRedirect />} />
              <Route path="search" element={<ShowSearch />} />
              <Route path=":showFolderId">
                <Route index element={<ShowList />} />
                <Route path="show/:showId">
                  <Route index element={<Navigate replace to="design" />} />
                  <Route path="design" element={<ShowDetailPage />} />
                  <Route path="preview" element={<ShowPreviewPage />} />
                  <Route path="settings" element={<ShowSettings />} />
                  <Route element={<PermissionFilter permission="canManage" />}>
                    <Route path="audit-logs" element={<ShowAuditLogs />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>

          <Route element={<PermissionFilter permission="canAccessStudio" />}>
            <Route path="studio" element={<StudioRoot />}>
              <Route index element={<StudioRootRedirect />} />
              <Route path="search" element={<DesignSearch />} />
              <Route path=":designFolderId">
                <Route index element={<StudioList />} />
                <Route path="design/:designId" element={<StudioLayout />}>
                  <Route index element={<StudioEdit />} />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="templates" element={<PermissionFilter permission="canAccessTemplates" />}>
            <Route index element={<TemplateList />} />
            <Route path=":templateId">
              <Route index element={<Navigate replace to="settings" />} />
              <Route path="configuration" element={<TemplateConfiguration />} />
              <Route path="settings" element={<TemplateSettings />} />
              <Route path="usage" element={<TemplateUsage />} />
              <Route path="versions" element={<TemplateVersions />} />
            </Route>
            <Route path=":templateId/create" element={<CreateTemplate />} />
          </Route>

          <Route path="tv-brands" element={<PermissionFilter permission="canManage" />}>
            <Route index element={<TVBrandList />} />
            <Route path="new" element={<NewTVBrand />} />
            <Route path=":tvBrandId">
              <Route index element={<Navigate replace to="details" />} />
              <Route path="details" element={<TVBrandSettings />} />
              <Route path="audit-logs" element={<TVBrandAuditLogs />} />
            </Route>
          </Route>

          <Route path="users" element={<PermissionFilter permission="canManage" />}>
            <Route index element={<UserList />} />
            <Route path=":userId">
              <Route index element={<Navigate replace to="details" />} />
              <Route path="details" element={<UserSettings />} />
              <Route path="audit-logs" element={<UserAuditLogs />} />
            </Route>
          </Route>

          <Route path="not-found" element={<NotFound />} />
          <Route path="*" element={<Navigate replace to="./not-found" />} />
        </Route>
      </Route>

      <Route path="not-found" element={<NotFound />} />
      <Route path="*" element={<Navigate replace to="/not-found" />} />
    </Route>,
  ),
);
